/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect } from 'react-router-dom';
import cx from 'classnames';

import {
    TextButton,
    Text,
    Spacer,
    Alert,
    CloseButton,
    Heading,
} from '@zillow/constellation';

import { Container, Col, Row } from '@zillow/zrs-comet';

// Constants
import { BILLING_PAGE } from 'app/shared/constants/RouteConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';
import LoadingConstants from 'app/shared/constants/LoadingConstants';

// Actions
import ModalActions from 'app/shared/actions/ModalActions';
import BillingActions from 'app/shared/actions/BillingActions';

// Components
import PermissionCheck from 'app/shared/components/PermissionCheck';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import { withErrorBoundary } from 'app/shared/components/ErrorBoundary';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

// Selectors
import { RoutingSelectors, UserSelectors, AppSelectors } from 'app/shared/selectors';

import { RMWContext } from 'app/shared/providers/RMWContext';

import PaymentDuePage from 'app/shared/containers/PaymentDuePage';
import PaidInvoicesPage from 'app/shared/containers/PaidInvoicesPage';
import PaymentSettingsPage from 'app/shared/containers/PaymentSettingsPage';

import SearchBox from 'app/shared/components/SearchBox';

import billingUtils from 'app/shared/utils/billingUtils';
import accessUtils from 'app/shared/utils/accessUtils';

import { StyledContainer } from './StyledComponents';

// Stylesheets
import './style.scss';

const DEFAULT_PAGE_TITLE = 'Billing';

const TabList = {
    paid: 'paid',
    due: 'due',
    paymentSettings: 'paymentSettings',
};

const propTypes = {
    router: PropTypes.object.isRequired,
};

const contextTypes = {
    paths: PropTypes.object,
};

class BillingPage extends React.Component {
    static contextType = RMWContext;

    componentWillUnmount() {
        const { dispatch } = this.props;
        dispatch(BillingActions.setDisplayPaymentSuccess(false));
        dispatch(BillingActions.initUserSettings());
    }

    loadTab = (tab) => {
        const baseDetailsUrl = this.context.paths.billingPage;
        const url = `${baseDetailsUrl}/${tab}`;
        return window.router.transitionTo(url);
    }

    getActiveTab() {
        const { router } = this.props;
        const { paths } = this.context;

        // Expecting path to be [baseUrl]/rental-manager/billing/*something*
        const tabName = router.location.pathname.split('/')[3];

        switch (tabName) {
            case paths.billingDue:
                return paths.billingDue;
            case paths.billingPaid:
                return paths.billingPaid;
            case 'paymentSettings':
                return 'paymentSettings';
            default:
                return paths.billingDue;
        }
    }

    getSectionUrl(section) {
        const { paths } = this.context;
        const baseDetailsUrl = paths.billingPage;
        return `${baseDetailsUrl}/${section}([?].*)?`;
    }

    renderChildRoutes() {
        const { paths } = this.context;
        const baseDetailsUrl = paths.billingPage;
        const isOnlyNucleusUser = billingUtils.isOnlyNucleusUser(this.props.user);

        // child paths
        const childPathDue = this.getSectionUrl(paths.billingDue);
        const childPathPaid = this.getSectionUrl(paths.billingPaid);
        const childPathPaymentSettings = this.getSectionUrl('paymentSettings');
        return (
            <Switch>
                <Route
                    render={() => {
                        if (isOnlyNucleusUser) {
                            return <Redirect to={`${baseDetailsUrl}/${paths.billingPaid}`} />;
                        }
                        return <Route component={PaymentDuePage} path={childPathDue} />;
                    }}
                    path={childPathDue}
                />
                <Route component={PaidInvoicesPage} path={childPathPaid} />
                <Route component={PaymentSettingsPage} path={childPathPaymentSettings} />
            </Switch>
        );
    }

    handlePayInfo = () => {
        const { dispatch } = this.props;
        dispatch(ModalActions.showModal({
            modalType: ModalConstants.PAYMENT_INFO_MODAL,
        }));
    }

    handleSearch = (searchText) => {
        const { dispatch } = this.props;
        dispatch(BillingActions.search(searchText));
    };

    handleSearchUpdate = (searchText) => {
        const { dispatch } = this.props;
        if (!searchText) dispatch(BillingActions.search(searchText));
    };

    isSearchEnabled = () => !billingUtils.isOnlyNucleusUser(this.props.user);

    renderPaymentInfo = () => {
        const { isScreenMobile } = this.props;
        if (isScreenMobile) return null;
        return (
            <li
                className="TabMenuItem-info TabMenuItem-Right TabMenuItem-note"
            >
                <Text fontType="bodySmall">
                    *Only credit card payment is supported for online payments right now.
                    For other types of payments, such as check, please follow the
                    instructions
                    {' '}
                    <TextButton onClick={this.handlePayInfo} fontType="bodySmall">here</TextButton>
                </Text>
            </li>
        );
    };

    renderSubNav = () => {
        const { paths } = this.context;
        const activeTab = this.getActiveTab();
        const { user } = this.props;
        const paymentsEnabled = accessUtils.canAccessInvoicePayments({ user });

        return (
            <Container className="BillingPage-SubNav">
                <Spacer paddingX="lg" marginY="xs">
                    <ul className="BillingPage-SubNav-list">
                        <li
                            className={cx('TabMenuItem-links', {
                                'TabMenuItem-active': activeTab === TabList.due,
                            })}
                            onClick={() => this.loadTab(paths.billingDue)}
                        >
                            <Text
                                fontType="body"
                                className={cx({ 'Utils-primary': activeTab === TabList.due })}
                            >
                                Payment Due
                            </Text>
                        </li>
                        <li
                            className={cx('TabMenuItem-links', {
                                'TabMenuItem-active': activeTab === TabList.paid,
                            })}
                            onClick={() => this.loadTab(paths.billingPaid)}
                        >
                            <Text
                                fontType="body"
                                className={cx({ 'Utils-primary': activeTab === TabList.paid })}
                            >
                                History
                            </Text>
                        </li>
                        {paymentsEnabled && (
                            <li
                                className={cx('TabMenuItem-links', {
                                    'TabMenuItem-active': activeTab === TabList.paymentSettings,
                                })}
                                onClick={() => this.loadTab('paymentSettings')}
                            >
                                <Text
                                    fontType="body"
                                    className={cx({ 'Utils-primary': activeTab === TabList.paymentSettings })}
                                >
                                    Settings
                                </Text>
                            </li>
                        )}
                        {paymentsEnabled ?
                            this.renderPaymentInfo()
                            : (
                                <li
                                    className="TabMenuItem-links TabMenuItem-Right"
                                    onClick={this.handlePayInfo}
                                >
                                    <Text
                                        fontType="body"
                                        className="Utils-primary"
                                    >
                                        How to pay?
                                    </Text>
                                </li>
                            )
                        }
                    </ul>
                </Spacer>
            </Container>
        );
    }


    renderContent() {
        const { currentInvoiceLoadingState, dispatch, displaySuccessMessage } = this.props;
        const isSearching = currentInvoiceLoadingState === LoadingConstants.LOADING;
        const isOnlyNucleusUser = billingUtils.isOnlyNucleusUser(this.props.user);
        return (
            <PermissionCheck route={BILLING_PAGE}>
                <Container className="BillingPage-container">
                    {displaySuccessMessage && (
                        <Row>
                            <Col xs={12}>
                                <Alert
                                    isOpen={true}
                                    appearance="success"
                                    body="Thank you for your payment!"
                                    onClose={() => dispatch(BillingActions.setDisplayPaymentSuccess(false))}
                                    closeButton={<CloseButton />}
                                />
                            </Col>
                        </Row>
                    )}
                    <StyledContainer className="BillingPage-card">
                        <Row>
                            <Col xs={7}>
                                <Spacer paddingX="lg">
                                    <Heading level="2" fontFamily="serif">{DEFAULT_PAGE_TITLE}</Heading>
                                </Spacer>
                            </Col>
                            <Col xs={5}>
                                <Spacer paddingX="lg">
                                    <div
                                        className={cx('BillingPage-search-loader', {
                                            'BillingPage-search-loading': isSearching,
                                        })}
                                    >
                                        {isSearching && <SpinnerContainer />}
                                    </div>
                                    {this.isSearchEnabled() && (
                                        <SearchBox
                                            placeholder="Search by Invoice No"
                                            onSearch={this.handleSearchUpdate}
                                            onSubmit={this.handleSearch}
                                        />
                                    )}
                                </Spacer>
                            </Col>
                        </Row>
                        {!isOnlyNucleusUser && this.renderSubNav()}
                        {this.renderChildRoutes()}
                    </StyledContainer>
                </Container>
            </PermissionCheck>
        );
    }

    render() {
        return (
            <AuthCheckWrapper requireStrongAuthentication={false}>
                <div className="BillingPage">
                    <HeadWrapper title={DEFAULT_PAGE_TITLE} />
                    {this.renderContent()}
                </div>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = state => ({
    router: RoutingSelectors.makeRouting(state),
    currentInvoiceLoadingState: state.billing.invoice.currentInvoiceLoadingState,
    user: UserSelectors.makeUser(state),
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
    displaySuccessMessage: state.billing.payment.displaySuccessMessage,
});

BillingPage.contextTypes = contextTypes;
BillingPage.propTypes = propTypes;

export { BillingPage };
export default withErrorBoundary(connect(mapStateToProps)(BillingPage));
